import React, { useState, useEffect } from "react";
import StyledSelect from "../../../../../components/Select/Select";
import { InputWrapper, Question } from "../VehicleDetails.style";
import styled from "styled-components";
import { StyledButton } from "../../../../../components/Button/Button";
import { useDispatch } from "react-redux";
import {
  setRto,
  setYear,
  setRegistrationDate,
  setIsRegistration,
  setPurchaseDate
} from "../../../../../modules";
import useUrlQuery from "../../../../../hooks/useUrlQuery";
import { useGetRTOQuery } from "../../../../../services/CarServices";
import { useTypedSelector } from "../../../../../hooks/useTypedSelector";
import MobileSectionLabel from "./MobileSectionLabel";
import { useMediaQuery } from "@mui/material";
import StyledRadioGroup from "../../../../../components/RadioGroup/StyledRadioGroup";
import { SelectOptions } from "../../../../../components/Select/Select";
import DatePicker from "../../../../../components/DatePicker/DatePicker";
import dayjs from "dayjs";
import moment from "moment";

const getLastFifteenYears = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i > currentYear - 15; i--) {
    years.push({ OPTION_KEY: i.toString(), OPTION_VAL: i.toString() });
  }
  return years;
};

const yesNoOptions: SelectOptions[] = [
  { OPTION_KEY: "Y", OPTION_VAL: "Yes" },
  { OPTION_KEY: "N", OPTION_VAL: "No" },
];

const Registration = () => {
  const dispatch = useDispatch();
  const { getUrlQuery, setUrlQuery } = useUrlQuery();
  const { rtoList } = useGetRTOQuery();
  const { rto, year, isRegistration, registrationDate, purchaseDate } = useTypedSelector(
    (state) => state.landing.vehicleDetails
  );

  const [errors, setErrors] = useState<{
    rto?: string;
    year?: string;
    registrationDate?: string;
    purchaseDate?: string;
  }>({});

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Ensure default value is "Y" on first render
  useEffect(() => {
    if (!isRegistration) {
      dispatch(setIsRegistration("Y"));
    }
  }, [dispatch, isRegistration]);

  // Safe date formatter using moment (handles Moment / Date / string)
  const formatDate = (val: any) => {
    if (!val) return "";
    const m = moment(val); // safe for moment objects and Date objects
    if (!m.isValid()) return "";
    return m.format("YYYY-MM-DD");
  };

  // helper to check valid stored date string (YYYY-MM-DD)
  const isValidStoredDate = (dateStr?: string) => {
    if (!dateStr) return false;
    const m = moment(dateStr, "YYYY-MM-DD", true); // strict parse
    return m.isValid();
  };

  const onClick = () => {
    let hasError = false;
    const newErrors: { rto?: string; year?: string; registrationDate?: string, purchaseDate?: string } = {};

    if (!rto?.number) {
      newErrors.rto = "Please select registration place";
      hasError = true;
    }
    if (!purchaseDate) {
      newErrors.purchaseDate = "Please select purchase date";
      hasError = true;
    }

    if (isRegistration === "Y") {
      // validate registration date (must be a valid YYYY-MM-DD)
      if (!isValidStoredDate(registrationDate)) {
        newErrors.registrationDate = "Please select a valid registration date";
        hasError = true;
      }
    } else {
      // validate year when not registered
      if (!year) {
        newErrors.year = "Please select registration year";
        hasError = true;
      }
    }

    setErrors(newErrors);

    if (hasError) return;

    if (getUrlQuery("flow") !== "new") {
      setUrlQuery("stage", "LEAD");
    }
  };

  // When user toggles Yes/No, clear the other input to avoid stale invalid values
  const handleRegistrationToggle = (val: string) => {
    dispatch(setIsRegistration(val));
    setErrors({}); // clear errors on change
    if (val === "Y") {
      // clear year
      dispatch(setYear(""));
    } else {
      // clear registration date
      dispatch(setRegistrationDate(""));
    }
  };

  return (
    <>
      {isMobile && (
        <MobileSectionLabel
          label="Enter your bike registration details"
          onClick={() => { }}
        />
      )}

      <Question>
        Registration <span>Place</span>
      </Question>
      <SelectWrapper>
        <StyledSelect
          label="Registration Place"
          size="small"
          options={rtoList}
          handleChange={(val) => {
            dispatch(
              setRto({
                number: val,
                name: rtoList.find((item) => item.OPTION_KEY === val)?.OPTION_VAL,
              })
            );
            setErrors((prev) => ({ ...prev, rto: "" }));
          }}
          selectedValue={rto.number}
        />
        {errors.rto && <ErrorText>{errors.rto}</ErrorText>}
      </SelectWrapper>

      <div className="ml-164">
        <StyledRadioGroup
          label="Is your vehicle already registered?"
          items={yesNoOptions}
          defaultValue={isRegistration || "Y"}
          name="isRegistration"
          handleChange={(event: string | React.ChangeEvent<HTMLInputElement>) => {
            const val = typeof event === "string" ? event : event.target?.value;
            handleRegistrationToggle(val);
          }}
        />
      </div>

      {/* Conditional Section */}
      {isRegistration === "Y" ? (
        <>
          <Question>
            Registration <span>Date</span>
          </Question>
          <InputWrapper>
            <DatePicker
              name="registrationDate"
              label=""
              size="small"
              disableFuture
              maxDate={moment()}
              onChange={(val: any) => {
                const formatted = formatDate(val);
                dispatch(setRegistrationDate(formatted));
                setErrors((prev) => ({ ...prev, registrationDate: "" }));
              }}
              value={
                registrationDate ? moment(registrationDate, "YYYY-MM-DD") : null
              }
            />
          </InputWrapper>
          <div className="ml-164">
            {errors.registrationDate && (
              <ErrorText>{errors.registrationDate}</ErrorText>
            )}
          </div>
        </>
      ) : (
        <>
          <Question>
            Registration <span>Year</span>
          </Question>
          <SelectWrapper>
            <StyledSelect
              label="Registration Year"
              options={getLastFifteenYears()}
              selectedValue={year}
              handleChange={(val) => {
                dispatch(setYear(val));
                setErrors((prev) => ({ ...prev, year: "" }));
              }}
            />
            {errors.year && <ErrorText>{errors.year}</ErrorText>}
          </SelectWrapper>
        </>
      )}
      <Question>
        Purchase <span>Date</span>
      </Question>
      <InputWrapper>
        <DatePicker
          name="purchaseDate"
          label=""
          size="small"
          disableFuture
          maxDate={moment()}
          onChange={(val: any) => {
            const formatted = formatDate(val);
            dispatch(setPurchaseDate(formatted));
            setErrors((prev) => ({ ...prev, purchaseDate: "" }));
          }}
          value={
            purchaseDate ? moment(purchaseDate, "YYYY-MM-DD") : null
          }
        />
      </InputWrapper>
      <div className="ml-165">
        {errors.purchaseDate && (
          <ErrorText>{errors.purchaseDate}</ErrorText>
        )}
      </div>

      {/* Submit */}
      <div className="w-200-m-auto">
        <StyledButton variant="contained" fullWidth onClick={onClick}>
          Save and Continue
        </StyledButton>
      </div>
    </>
  );
};

export default Registration;

const SelectWrapper = styled.div`
  width: 60%;
  margin: 0 auto 30px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ErrorText = styled.p`
  color: red;
  font-size: 13px;
  margin: 5px 0 0;
`;

